import type {
  CustomerExperienceScoreAggregate,
  CustomerExperienceScoreDistributionEntry,
} from "@repo/app-types/entities/topic";
import { useMemo, type FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  CUSTOMER_EXPERIENCE_SCORE_LABEL,
  type CustomerExperienceScoreValue,
} from "../../teammatePerformance/gradingCustomerExperienceScorePill.js";

interface TopicCustomerExperienceScoreBreakdownProps {
  distribution: readonly CustomerExperienceScoreDistributionEntry[];
  baseline: CustomerExperienceScoreAggregate | null;
}

interface ScoreRow {
  score: CustomerExperienceScoreValue;
  label: string;
  callCount: number;
  percent: number;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function getDeltaToneClass(delta: number | null): string {
  if (delta === null || delta === 0) {
    return "text-muted-foreground";
  }
  if (delta > 0) {
    return "text-emerald-600";
  }
  return "text-destructive";
}

export const TopicCustomerExperienceScoreBreakdown: FC<
  TopicCustomerExperienceScoreBreakdownProps
> = ({ distribution, baseline }) => {
  const { topicCount, topicAverage, overallAverage, delta } = useMemo(() => {
    const count = distribution.reduce((sum, e) => sum + e.count, 0);
    const sum = distribution.reduce((s, e) => s + e.score * e.count, 0);
    const topicAvg = count > 0 ? round1(sum / count) : null;
    const overallAvg =
      baseline && baseline.callCount > 0
        ? round1(baseline.sum / baseline.callCount)
        : null;
    return {
      topicCount: count,
      topicAverage: topicAvg,
      overallAverage: overallAvg,
      delta:
        topicAvg !== null && overallAvg !== null ? topicAvg - overallAvg : null,
    };
  }, [distribution, baseline]);

  if (topicCount === 0) {
    return (
      <p className="rounded-md border border-border-light bg-white p-4 text-sm text-muted-foreground">
        No scored calls in this period.
      </p>
    );
  }

  // Sort descending so the rows read best-at-the-top regardless of the
  // upstream order.
  const rows: readonly ScoreRow[] = [...distribution]
    .sort((a, b) => b.score - a.score)
    .map((entry) => ({
      score: entry.score,
      label: CUSTOMER_EXPERIENCE_SCORE_LABEL[entry.score],
      callCount: entry.count,
      percent: (entry.count / topicCount) * 100,
    }));

  const deltaToneClass = getDeltaToneClass(delta);
  // Always show a sign — a flat `0.0` still reads as `+0.0` so the
  // headline arithmetic is unambiguous.
  const deltaPrefix = delta === null ? "" : delta < 0 ? "−" : "+";

  return (
    <div className="rounded-md border border-border-light bg-white p-4">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl leading-none font-semibold text-foreground tabular-nums">
          {topicAverage !== null ? topicAverage.toFixed(1) : "—"}
        </span>
        {delta !== null ? (
          <span className={twMerge("text-sm tabular-nums", deltaToneClass)}>
            {deltaPrefix}
            {Math.abs(delta).toFixed(1)}
          </span>
        ) : null}
        {overallAverage !== null ? (
          <span className="text-sm text-muted-foreground">
            vs overall ({overallAverage.toFixed(1)})
          </span>
        ) : null}
      </div>

      <ul className="mt-4 space-y-2">
        {rows.map((row) => {
          const wholePct = Math.round(row.percent);
          const displayPct =
            row.callCount > 0 && wholePct === 0 ? "<1%" : `${wholePct}%`;
          return (
            <li
              key={row.score}
              className="grid grid-cols-[6.75rem_minmax(0,1fr)_2.5rem] items-center gap-3 text-sm text-foreground"
            >
              <span className="flex items-baseline gap-2">
                <span className="text-muted-foreground tabular-nums">
                  {row.score}
                </span>
                <span>{row.label}</span>
              </span>
              <span
                className="h-1.5 rounded-full bg-gray-100"
                role="img"
                aria-label={`${displayPct} of scored calls`}
              >
                <span
                  className="block h-full rounded-full bg-gray-400"
                  style={{ width: `${row.percent}%` }}
                />
              </span>
              <span className="text-right font-medium text-gray-600 tabular-nums">
                {displayPct}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
